import { KeyboardEvent, ReactNode, useEffect, useId, useRef } from 'react'
import cx from 'classnames'
import styles from './ConfirmDialog.module.css'

/**
 * 시안에 없는 신규 컴포넌트 — 조치형 화면(Hold 해제·스위치 토글 등)의 확인 단계.
 * `<dialog>` 대신 고정 오버레이 div 를 쓴다. 백드롭 클릭 닫기·포커스 트랩·애니메이션은
 * 요구되지 않아 넣지 않았다(브리프 범위 밖).
 */
export type ConfirmDialogProps = {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null => {
  const titleId = useId()
  const bodyId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  // 열릴 때 포커스를 패널로 옮긴다 — 이게 없으면 Esc 가 트리거 버튼 등 바깥 요소에서만
  // 잡혀 다이얼로그까지 도달하지 않는다. 포커스 트랩(순환·격리)은 아니다 — 최초 이동뿐이다.
  useEffect(() => {
    if (open) {
      panelRef.current?.focus()
    }
  }, [open])

  if (!open) {
    return null
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <div className={styles.overlay}>
      <div
        ref={panelRef}
        tabIndex={-1}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={handleKeyDown}
      >
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <div id={bodyId} className={styles.body}>
          {body}
        </div>
        <div className={styles.actions}>
          <button type="button" className={cx(styles.btn, styles.cancel)} onClick={onCancel}>
            취소
          </button>
          <button type="button" className={cx(styles.btn, styles.confirm, danger && styles.danger)} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
